import React from 'react';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const ReviewSubmitStep = ({ formData, termsAccepted, onTermsChange }) => {
  const sections = [
    {
      title: 'Personal Information',
      icon: '👤',
      color: 'blue',
      fields: [
        { label: 'Full Name', value: `${formData?.title} ${formData?.firstName} ${formData?.middleName} ${formData?.lastName}`?.trim() },
        { label: 'Date of Birth', value: formData?.dateOfBirth },
        { label: 'Gender', value: formData?.gender },
        { label: 'Marital Status', value: formData?.maritalStatus },
        { label: 'Nationality', value: formData?.nationality }
      ]
    },
    {
      title: 'Contact Information',
      icon: '📧',
      color: 'violet',
      fields: [
        { label: 'Email', value: formData?.email },
        { label: 'Phone', value: formData?.phone },
        { label: 'Address', value: `${formData?.streetAddress}, ${formData?.city}, ${formData?.state}` }
      ]
    },
    {
      title: 'Educational Background',
      icon: '🎓',
      color: 'emerald',
      fields: [
        { label: 'Institution', value: formData?.institutionName },
        { label: 'Degree', value: `${formData?.degreeType} in ${formData?.courseOfStudy}` },
        { label: 'Year of Graduation', value: formData?.graduationYear },
        { label: 'Class of Degree', value: formData?.classOfDegree },
        { label: 'CGPA', value: formData?.cgpa }
      ]
    },
    {
      title: 'NYSC Information',
      icon: '🎖️',
      color: 'amber',
      fields: [
        { label: 'Call-Up Number', value: formData?.nyscCallUpNumber },
        { label: 'Service Batch', value: formData?.serviceBatch },
        { label: 'State of Deployment', value: formData?.stateOfDeployment },
        { label: 'PPA Name', value: formData?.ppaName }
      ]
    },
    {
      title: 'Emergency Contact',
      icon: '🚨',
      color: 'red',
      fields: [
        { label: 'Name', value: formData?.emergencyContactName },
        { label: 'Relationship', value: formData?.emergencyContactRelationship },
        { label: 'Phone', value: formData?.emergencyContactPhone }
      ]
    }
  ];

  const colorClasses = {
    blue: 'bg-blue-50 border-blue-200',
    violet: 'bg-violet-50 border-violet-200',
    emerald: 'bg-emerald-50 border-emerald-200',
    amber: 'bg-amber-50 border-amber-200',
    red: 'bg-red-50 border-red-200'
  };

  const PersonalInfoSection = () => (
    <div className={`border rounded-lg p-6 ${colorClasses?.[sections?.[0]?.color]}`}>
      <div className="flex items-center gap-3 mb-4">
        <span className="text-2xl">{sections?.[0]?.icon}</span>
        <h4 className="text-lg font-semibold text-gray-900">{sections?.[0]?.title}</h4>
      </div>
      <div className="space-y-3">
        {sections?.[0]?.fields?.map((field, fieldIndex) => (
          <div key={fieldIndex} className="flex justify-between items-start py-2 border-b border-gray-200 last:border-0">
            <span className="text-sm font-medium text-gray-600">{field?.label}:</span>
            <span className="text-sm text-gray-900 text-right max-w-xs">{field?.value || 'Not provided'}</span>
          </div>
        ))}
      </div>
    </div>
  );

  const ContactInfoSection = () => (
    <div className={`border rounded-lg p-6 ${colorClasses?.[sections?.[1]?.color]}`}>
      <div className="flex items-center gap-3 mb-4">
        <span className="text-2xl">{sections?.[1]?.icon}</span>
        <h4 className="text-lg font-semibold text-gray-900">{sections?.[1]?.title}</h4>
      </div>
      <div className="space-y-3">
        {sections?.[1]?.fields?.map((field, fieldIndex) => (
          <div key={fieldIndex} className="flex justify-between items-start py-2 border-b border-gray-200 last:border-0">
            <span className="text-sm font-medium text-gray-600">{field?.label}:</span>
            <span className="text-sm text-gray-900 text-right max-w-xs">{field?.value || 'Not provided'}</span>
          </div>
        ))}
      </div>
    </div>
  );

  const EducationSection = () => (
    <div className={`border rounded-lg p-6 ${colorClasses?.[sections?.[2]?.color]}`}>
      <div className="flex items-center gap-3 mb-4">
        <span className="text-2xl">{sections?.[2]?.icon}</span>
        <h4 className="text-lg font-semibold text-gray-900">{sections?.[2]?.title}</h4>
      </div>
      <div className="space-y-3">
        {sections?.[2]?.fields?.map((field, fieldIndex) => (
          <div key={fieldIndex} className="flex justify-between items-start py-2 border-b border-gray-200 last:border-0">
            <span className="text-sm font-medium text-gray-600">{field?.label}:</span>
            <span className="text-sm text-gray-900 text-right max-w-xs">{field?.value || 'Not provided'}</span>
          </div>
        ))}
      </div>
    </div>
  );

  const NYSCInfoSection = () => (
    <div className={`border rounded-lg p-6 ${colorClasses?.[sections?.[3]?.color]}`}>
      <div className="flex items-center gap-3 mb-4">
        <span className="text-2xl">{sections?.[3]?.icon}</span>
        <h4 className="text-lg font-semibold text-gray-900">{sections?.[3]?.title}</h4>
      </div>
      <div className="space-y-3">
        {sections?.[3]?.fields?.map((field, fieldIndex) => (
          <div key={fieldIndex} className="flex justify-between items-start py-2 border-b border-gray-200 last:border-0">
            <span className="text-sm font-medium text-gray-600">{field?.label}:</span>
            <span className="text-sm text-gray-900 text-right max-w-xs">{field?.value || 'Not provided'}</span>
          </div>
        ))}
      </div>
    </div>
  );

  const EmergencyContactSection = () => (
    <div className={`border rounded-lg p-6 ${colorClasses?.[sections?.[4]?.color]}`}>
      <div className="flex items-center gap-3 mb-4">
        <span className="text-2xl">{sections?.[4]?.icon}</span>
        <h4 className="text-lg font-semibold text-gray-900">{sections?.[4]?.title}</h4>
      </div>
      <div className="space-y-3">
        {sections?.[4]?.fields?.map((field, fieldIndex) => (
          <div key={fieldIndex} className="flex justify-between items-start py-2 border-b border-gray-200 last:border-0">
            <span className="text-sm font-medium text-gray-600">{field?.label}:</span>
            <span className="text-sm text-gray-900 text-right max-w-xs">{field?.value || 'Not provided'}</span>
          </div>
        ))}
      </div>
    </div>
  );

  const DocumentSection = () => (
    <div className="space-y-3">
      <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
        <Icon name="FileText" size={18} color="#6366F1" />
        Uploaded Documents
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {[
          { label: 'Graduation Certificate', field: 'tertiaryGraduationCert' },
          { label: 'Birth Certificate', field: 'birthCertificate' },
          { label: 'NYSC Assignment Letter', field: 'nyscAssignmentLetter' },
          { label: 'Means of ID', field: 'meansOfId' },
          { label: 'Passport Photo', field: 'passportPhoto' },
          { label: 'Recent Photo 1', field: 'recentPhoto1' },
          { label: 'Recent Photo 2', field: 'recentPhoto2' }
        ]?.map((doc, index) => (
          <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-2">
              <Icon 
                name={formData?.[doc?.field]?.type?.includes('pdf') ? 'FileText' : 'Image'} 
                size={16} 
                color="#6B7280" 
              />
              <div>
                <p className="text-xs font-medium text-gray-900">{doc?.label}</p>
                <p className="text-xs text-gray-500">
                  {formData?.[doc?.field]?.name || 'Not uploaded'}
                </p>
              </div>
            </div>
            {formData?.[doc?.field] && (
              <Icon name="CheckCircle" size={16} color="#10B981" />
            )}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Review & Submit
        </h2>
        <p className="text-sm text-gray-600">
          Please review all the information carefully before submitting your application
        </p>
      </div>

      <div className="space-y-6">
        <PersonalInfoSection />
        <ContactInfoSection />
        <EducationSection />
        <NYSCInfoSection />
        <EmergencyContactSection />
        <DocumentSection />
      </div>

      <div className="bg-white border-2 border-gray-300 rounded-lg p-6">
        <h4 className="text-lg font-semibold text-gray-900 mb-4">Terms and Conditions</h4>
        <div className="bg-gray-50 rounded-lg p-4 mb-4 max-h-64 overflow-y-auto">
          <div className="text-sm text-gray-700 space-y-3">
            <p className="font-medium">By submitting this form, you agree to the following:</p>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li>All information provided is accurate and truthful to the best of your knowledge</li>
              <li>You authorize the company to verify the information provided</li>
              <li>You consent to the processing of your personal data in accordance with our Privacy Policy</li>
              <li>You understand that providing false information may result in termination of employment</li>
              <li>You agree to notify the company of any changes to the information provided</li>
              <li>You have read and understood the company's code of conduct and policies</li>
            </ul>
            <p className="mt-4 text-xs text-gray-600">
              Your data will be processed in accordance with the Nigeria Data Protection Regulation (NDPR) and will be kept confidential. For more information, please refer to our Privacy Policy.
            </p>
          </div>
        </div>
        <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg mb-4">
          <div className="flex-shrink-0 mt-0.5">
            <Icon name="AlertTriangle" size={20} color="#F59E0B" />
          </div>
          <p className="text-sm text-amber-800">
            <strong>Important:</strong> You must check the box below to accept the terms and conditions before you can submit your application.
          </p>
        </div>
        <Checkbox
          label="I have read and agree to the terms and conditions"
          description="You must accept the terms to submit your application"
          checked={termsAccepted}
          onChange={(e) => {
            console.log('Terms checkbox changed:', e?.target?.checked);
            onTermsChange(e?.target?.checked);
          }}
          required
          error={!termsAccepted && "Please accept the terms and conditions to continue"}
        />
      </div>
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <div className="text-blue-500">
            <Icon name="Info" size={20} />
          </div>
          <div className="text-sm text-gray-700">
            <p className="font-medium text-gray-900 mb-1">What happens next?</p>
            <p>
              After submission, you'll receive a confirmation email with your application reference number. Our HR team will review your application and contact you within 3-5 business days.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewSubmitStep;